import React, { useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { istDate } from "@/components/DispatchDatePicker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const today = () => istDate(0);
const monthStart = () => `${today().slice(0, 8)}01`;
const pastMonths = () => {
  const [y, m] = today().split("-").map(Number);
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 2 - i, 1));
    const from = d.toISOString().slice(0, 10);
    const to = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
    return { from, to, label: d.toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" }) };
  });
};
const chip = "text-xs px-3 py-1.5 rounded-full border border-[#DCDCD3] hover:bg-[#F0F0EA] disabled:opacity-50";
const box = "border border-[#DCDCD3] rounded px-2 py-1 text-xs min-w-0";
const go = "text-xs px-3 py-1.5 rounded bg-[#385C4B] text-white hover:bg-[#2c4a3c] disabled:opacity-50 flex items-center gap-1 shrink-0";

function PeriodPicker({ kind, prefix, busy, run }) {
  const months = pastMonths();
  const [month, setMonth] = useState(months[0].from);
  const [c, setC] = useState({ from: monthStart(), to: today() });
  const btn = (testid, range, label) => <button disabled={busy} onClick={() => run(range)} className={chip} data-testid={`${prefix}-${kind}-${testid}`}>{label}</button>;
  return (
    <div className="space-y-3" data-testid={`${prefix}-${kind}-menu`}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">Download {kind === "pdf" ? "PDF" : "Excel"} · choose period</div>
      <div className="flex flex-wrap gap-2">
        {btn("this-month", { from: monthStart(), to: today() }, "This Month")}
        {btn("all-time", { from: "", to: today() }, "All Time")}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs w-14 shrink-0">Month</span>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={`${box} flex-1 bg-white`} data-testid={`${prefix}-${kind}-month-select`}>
          {months.map((m) => <option key={m.from} value={m.from}>{m.label}</option>)}
        </select>
        <button disabled={busy} onClick={() => run(months.find((m) => m.from === month))} className={go} data-testid={`${prefix}-${kind}-month-go`}><Download className="w-3 h-3" />Go</button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs w-14 shrink-0">Custom</span>
        <input type="date" value={c.from} max={c.to} onChange={(e) => setC({ ...c, from: e.target.value })} className={`${box} flex-1`} data-testid={`${prefix}-${kind}-custom-from`} />
        <span className="text-xs">to</span>
        <input type="date" value={c.to} max={today()} onChange={(e) => e.target.value && setC({ ...c, to: e.target.value })} className={`${box} flex-1`} data-testid={`${prefix}-${kind}-custom-to`} />
        <button disabled={busy || !c.from} onClick={() => run(c)} className={go} data-testid={`${prefix}-${kind}-custom-go`}><Download className="w-3 h-3" />Go</button>
      </div>
      {busy && <div className="text-xs text-[#6B6B61] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Preparing file…</div>}
    </div>
  );
}

export function DownloadMenu({ prefix, fetcher, onExcel, onPdf }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = (kind) => async (range) => {
    setBusy(true);
    try {
      const data = await fetcher(range);
      await (kind === "pdf" ? onPdf : onExcel)(data, range.from, range.to);
      setOpen(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const trigger = "text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-[#DCDCD3] hover:bg-[#F0F0EA]";
  return (
    <div className="flex gap-2">
      {[["excel", "Excel", FileSpreadsheet, "text-[#385C4B]"], ["pdf", "PDF", FileText, "text-[#C8553D]"]].map(([kind, label, Icon, cls]) => (
        <Popover key={kind} open={open === kind} onOpenChange={(o) => setOpen(o ? kind : null)}>
          <PopoverTrigger asChild>
            <button className={trigger} data-testid={`${prefix}-${kind}`}><Icon className={`w-3.5 h-3.5 ${cls}`} />{label}</button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[420px] max-w-[calc(100vw-2rem)]">
            <PeriodPicker kind={kind} prefix={prefix} busy={busy} run={run(kind)} />
          </PopoverContent>
        </Popover>
      ))}
    </div>
  );
}
